import { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Banner from '../../../components/banner/Banner';
import LoginModal from '../../../components/modals/LoginModal';
import { useGetPublicLessonPlanQuery } from '../../../services/endpoints/content/lessonPlan';
import { useGetRecipeQuery } from '../../../services/endpoints/content/recipe';
import { useGetAllSnippetsQuery } from '../../../services/endpoints/website/snippet';
import { getCurrentUser } from '../../../services/helper';
import { useAppSelector } from '../../../services/hooks';
import { selectModal } from '../../../services/slicers/modalSlice';
import { LessonPlan, Recipe } from '../../../types/lessonPlan.type';
import { Snippet } from '../../../types/website.types';
import { getPrimaryRecipe, getLessonPlanRecipeListTitle, getRecipeImage, sortLessonPlanRecipes } from '../../../utils/utils';
import { PageMeta } from '../../../components/meta/PageMeta';
import LessonPlanChatter from './LessonPlanChatter';
import LessonPlanChecklist from './LessonPlanChecklist';
import LessonPlanNotes from './LessonPlanNotes';
import LessonPlanShoppingList from './LessonPlanShoppingList';
import LessonPlanTimeline from './LessonPlanTimeline';
import RecipeEquipment from './RecipeEquipment';
import RecipeFacts from './RecipeFacts';
import RecipeHistory from './RecipeHistory';
import RecipeIngredients from './RecipeIngredients';
import RecipeJokes from './RecipeJokes';
import RecipeSkills from './RecipeSkills';
import RecipeSocial from './RecipeSocial';
import RecipeStats from './RecipeStats';
import RecipeSteps from './RecipeSteps';
import RecipeStory from './RecipeStory';
import RecipeSubstitutions from './RecipeSubstitutions';
import RecipeSurprise from './RecipeSurprise';
import './SingleRecipe.css';

const currentUser = getCurrentUser();

interface SingleRecipeProps {
  isLessonPlan?: boolean;
}
const SingleRecipe: React.FC<SingleRecipeProps> = ({ isLessonPlan }) => {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [lessonPlan, setLessonPlan] = useState<LessonPlan | null>(null);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [scale, setScale] = useState(1);
  const params = useParams();
  const navigate = useNavigate();
  const slugOrId = params.slug ?? params.id ?? '';
  const { data: recipeData } = useGetRecipeQuery(slugOrId, { skip: isLessonPlan });
  const { data: lessonPlanData } = useGetPublicLessonPlanQuery(slugOrId, {
    skip: !isLessonPlan
  });
  const { data: snippetsData } = useGetAllSnippetsQuery(
      {
        data: {
          filterBy: [
            {
              operator: 'includes',
              field: 'name',
              value: ['no-prep', 'nut-free']
            }
          ]
        }
      },
      { skip: !isLessonPlan && !currentUser?.hasDashAccess }
  );
  const showModal = useAppSelector(selectModal);

  useEffect(() => {
    if (recipeData?.recipe) {
      setRecipe(recipeData.recipe);
    }
    if (lessonPlanData?.lessonPlan) {
      setLessonPlan(lessonPlanData.lessonPlan);
      const findPrimaryRecipe = getPrimaryRecipe(lessonPlanData.lessonPlan);
      if (findPrimaryRecipe) {
        // setPrimaryRecipe(findPrimaryRecipe);
        setRecipe(findPrimaryRecipe);
      }
    }
  }, [recipeData, lessonPlanData]);

  useEffect(() => {
    if (snippetsData?.results) {
      setSnippets(snippetsData.results);
    }
  }, [snippetsData]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setScale(1);
  }, [slugOrId]);

  // Prefer slug URLs when the page was opened via numeric id or a non-canonical slug.
  useEffect(() => {
    if (isLessonPlan && lessonPlan?.slug && slugOrId !== lessonPlan.slug) {
      navigate(`/lesson-plans/${lessonPlan.slug}`, { replace: true });
      return;
    }
    if (!isLessonPlan && recipe?.slug && slugOrId !== recipe.slug) {
      navigate(`/recipes/${recipe.slug}`, { replace: true });
    }
  }, [isLessonPlan, lessonPlan, recipe, slugOrId, navigate]);

  const NAVBAR_OFFSET = 80;
  const scrollToElement = (elementId: string = 'ingredients') => {
    const element = document.getElementById(elementId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleRedirect = () => {
    window.location.href =
        'https://www.amazon.com/stores/author/B093YJHWSM/allbooks?ingress=0&visitId=40d233ce-6f77-44b4-88e1-623a39c5edc5&store_ref=ap_rdr&ref_=ap_rdr';
  };

  const sortedLessonPlanRecipes = lessonPlan ? sortLessonPlanRecipes(lessonPlan.recipes) : [];
  const lessonPlanRecipeListTitle = lessonPlan
    ? getLessonPlanRecipeListTitle(sortedLessonPlanRecipes)
    : null;

  return (
      <>
        {recipe && (
            <PageMeta
                title={`Kid-friendly ${lessonPlan ? lessonPlan.name : recipe.title} Recipe`}
                description={`Cooking with kids! Learn to make this kid-friendly ${lessonPlan ? lessonPlan.name : recipe.title} recipe today.`}
                ogImage={getRecipeImage(recipe, 'original') ?? undefined}
            />
        )}
        <LoginModal show={showModal.show} />
        {recipe && (
            <div style={{ backgroundColor: '#FFF' }}>
              <Banner
                  title={lessonPlan ? lessonPlan.name : recipe.title}
                  pageTitle={`${isLessonPlan ? (currentUser?.hasDashAccess ? 'Lesson Plan' : 'Family Meal Plan') : 'Recipe'}: ${
                      lessonPlanRecipeListTitle ?? recipe.title
                  }`}
                  mainPage={
                    isLessonPlan ? (currentUser?.hasDashAccess ? 'Dashboard' : 'Recipes') : 'Recipes'
                  }
                  hideBreadcrumb
                  mainPageUrl={
                    isLessonPlan ? (currentUser?.hasDashAccess ? '/admin' : '/recipes') : '/recipes'
                  }
              />
              <div className="section-content">
                <div className="container">
                  <Row>
                    <Col lg={8}>
                      <div>
                        <h2
                            className="text-info fw-bold"
                            style={{ marginBottom: '20px' }}
                        >
                          {lessonPlanRecipeListTitle ?? recipe.title}
                        </h2>
                        <div className="subheader">by {recipe.author}</div>
                        <RecipeSocial
                            recipe={recipe}
                            lessonPlan={lessonPlan}
                        />
                      </div>
                      <div>
                        {recipe.recipe_images?.length && (
                            <>
                              <img
                                  src={getRecipeImage(recipe, 'original') ?? ''}
                                  alt={recipe.title}
                                  className="image-recipe"
                              />
                              <div className="subheader tiny align-left">
                                Photo By {recipe.recipe_images[0].source}
                              </div>
                            </>
                        )}
                      </div>
                      <RecipeStats
                          recipe={recipe}
                          lessonPlan={lessonPlan}
                      />
                      {recipe.story && (
                          <RecipeStory
                              recipe={recipe}
                              scrollToRecipe={() => scrollToElement("ingredients")}
                          />
                      )}
                      {lessonPlan?.shopping_list_items?.length && (
                          <LessonPlanShoppingList lessonPlan={lessonPlan} />
                      )}
                      {lessonPlan && currentUser?.hasDashAccess && (
                          <LessonPlanChecklist
                              lessonPlan={lessonPlan}
                              recipe={recipe}
                              scrollToSteps={(id) => scrollToElement(id)}
                              snippets={snippets}
                          />
                      )}
                      {lessonPlan && currentUser?.hasDashAccess && (
                          <LessonPlanTimeline
                              lessonPlan={lessonPlan}
                              scrollToSteps={(id) => scrollToElement(id)}
                              snippets={snippets}
                          />
                      )}
                      {!lessonPlan && recipe.kitchen_skills.length > 0 && (
                          <RecipeSkills recipe={recipe} />
                      )}
                      {lessonPlan?.recipes?.length && <RecipeSkills lessonPlan={lessonPlan} />}
                      {lessonPlan?.notes && currentUser?.hasDashAccess && (
                          <LessonPlanNotes
                              lessonPlan={lessonPlan}
                              scrollToSteps={(id) => scrollToElement(id)}
                          />
                      )}
                      <RecipeEquipment
                          recipe={recipe}
                          lessonPlan={lessonPlan}
                      />
                      {!isLessonPlan && <RecipeIngredients recipes={[recipe]} scale={scale} onScaleChange={setScale} />}
                      {lessonPlan && (
                          <RecipeIngredients
                              recipes={sortedLessonPlanRecipes}
                              scale={scale}
                              onScaleChange={setScale}
                          />
                      )}
                      {!lessonPlan && recipe.substitutions && (
                          <RecipeSubstitutions recipes={[recipe]} />
                      )}
                      {lessonPlan && lessonPlan.recipes.some((r) => r.substitutions) && (
                          <RecipeSubstitutions recipes={sortedLessonPlanRecipes} />
                      )}
                      {!lessonPlan && <RecipeSteps recipes={[recipe]} scale={scale} />}
                      {lessonPlan && <RecipeSteps recipes={sortedLessonPlanRecipes} scale={scale} />}
                      {lessonPlan && currentUser?.hasDashAccess && (
                          <LessonPlanChatter scrollToSteps={(id) => scrollToElement(id)} />
                      )}
                      {recipe.featured_ingredient && (
                          <RecipeSurprise
                              recipe={recipe}
                              scrollToRecipe={() => scrollToElement("ingredients")}
                          />
                      )}
                      {recipe.food_history && <RecipeHistory recipe={recipe} />}
                      {recipe.country_fact && <RecipeFacts recipe={recipe} />}
                      <RecipeSocial
                          recipe={recipe}
                          lessonPlan={lessonPlan}
                      />
                    </Col>
                    <Col
                        lg={4}
                        className="recipe-child-sidebar"
                    >
                      <div className="recipe-child-sidebar">
                        <RecipeJokes
                            lessonPlan={lessonPlan}
                            recipe={recipe}
                        />
                        <div className="sidebar-cta">
                          <div className="break"></div>
                          <div>
                            <h4 className="text-info fw-bolder mb-2 mt-4">Shop Our Cookbooks</h4>
                            <div>
                              Now available on Amazon! Our cookbooks feature kid-tested recipes that
                              build confidence in the kitchen. Expand your child&#x27;s palate and spark
                              a love of healthy foods with a Sticky Fingers Cooking cookbook.
                            </div>
                            <Button
                                variant="primary"
                                className="shop-button mt-3"
                                onClick={handleRedirect}
                            >
                              SHOP NOW
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
        )}
      </>
  );
};

export default SingleRecipe;